import {
  Check as CheckIcon,
  ContentCopy as ContentCopyIcon,
  Report,
  Schedule,
  Verified,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";
import React from "react";
import { Link, NavigateFunction } from "react-router-dom";
import Header from "../SharedComponents/Header/Header";
import LoginGuard, { logout } from "../SharedComponents/LoginGuard/loginGuard";
import { withNavigationHooks } from "../SharedComponents/withNavigationHooks";
import { environment } from "../utils/environment";
import {
  AssignmentController,
  AssignmentControllerDelegate,
  IAssignment,
} from "./controller";

interface State {
  error: {
    message?: string;
  };
  data: {
    assignments: IAssignment[];
  };
  isLoading: boolean;
  copiedRafeedback: { [key: string]: boolean };
}

interface Props {
  navigationHooks: NavigateFunction;
}

class Assignemnt
  extends React.Component<Props, State>
  implements AssignmentControllerDelegate {
  private controller: AssignmentController;

  constructor(props: Props) {
    super(props);
    this.controller = new AssignmentController(this);
    this.state = {
      error: {},
      data: {
        assignments: [],
      },
      isLoading: true,
      copiedRafeedback: {},
    };
  }

  componentDidMount(): void {
    const token = localStorage.getItem("AccessToken")!;
    this.controller.getAssignments(token);
  }

  seducNoAssignment(): void {
    this.setState({ isLoading: true }); // Ativa o loading
    window.location.href = `${environment.baseUrl}/seducSelect`;
  }

  parceirosNoAssignment(): void {
    this.setState({ isLoading: true });
    window.location.href = `${environment.baseUrl}/parceirosSelect`;
  }

  doutoradoNoAssignment(): void {
    this.setState({ isLoading: true, error: {} });
    // window.location.href = `${environment.baseUrl}/doutoradoSelect`;
    this.controller.triggerAutomaticDoutoradoAssignment();
  }

  noInstrumentsConfiguredError(message: string): void {
    this.setState({
      isLoading: false,
      error: { message: message },
    });
  }

  genericError(message?: string): void {
    this.setState({
      isLoading: false,
      error: {
        message:
          message ||
          "Ocorreu um erro inesperado. Por favor, tente recarregar a página.",
      },
    });
  }

  // Delegate
  didGetAssignments(assignments: IAssignment[]): void {
    this.setState({
      isLoading: false,
      error: {},
      data: {
        ...this.state.data,
        assignments,
      },
    });
  }

  applicationShouldLogout(): void {
    logout();
  }

  // Internals
  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  handleCopyRa = (raToCopy: string | undefined, cardKey: string) => {
    if (!raToCopy) return;

    navigator.clipboard
      .writeText(raToCopy)
      .then(() => {
        this.setState((prevState) => ({
          copiedRafeedback: {
            ...prevState.copiedRafeedback,
            [cardKey]: true,
          },
        }));

        setTimeout(() => {
          this.setState((prevState) => ({
            copiedRafeedback: {
              ...prevState.copiedRafeedback,
              [cardKey]: false,
            },
          }));
        }, 2000);
      })
      .catch((err) => {
        console.error("Falha ao copiar RA: ", err);
      });
  };

  renderStateMessage(
    title: string,
    message: string,
    options: { isError?: boolean } = {}
  ) {
    const { isError = false } = options;

    return (
      <Box
        mt={6}
        px={4}
        py={6}
        maxWidth={480}
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        borderRadius={4}
        sx={{
          border: (theme) =>
            `1px dashed ${isError ? theme.palette.error.main : grey[300]
            }`,
          bgcolor: (theme) =>
            isError ? alpha(theme.palette.error.light, 0.1) : grey[50],
        }}
      >
        <Box
          component="img"
          src="/images/not-found.png"
          alt={title}
          sx={{ width: 160, mb: 2 }}
        />
        <Typography
          variant="h5"
          color={isError ? "error" : "textPrimary"}
          gutterBottom
        >
          {title}
        </Typography>
        <Typography
          variant="body1"
          color={isError ? "error" : "textSecondary"}
        >
          {message}
        </Typography>
      </Box>
    );
  }

  renderAssignmentsCards() {
    if (this.state.data.assignments.length === 0) {
      return this.renderStateMessage(
        "Nenhum instrumento disponível",
        "Verifique novamente mais tarde ou entre em contato com o aplicador caso esteja aguardando novos instrumentos."
      );
    }

    const cards = this.state.data.assignments.map((assignment, index) => {
      const cardKey =
        assignment.cardType === "external_link"
          ? `card-external-${assignment.title}-${index}`
          : `card-internal-${assignment.assignmentId}-${index}`;

      if (assignment.cardType === "external_link") {
        const raValue = assignment.studentSeducId || "";
        const isCopied = this.state.copiedRafeedback[cardKey];

        return (
          <Box
            mt={3}
            border="solid 1px"
            borderColor={grey[300]}
            display="flex"
            flexDirection="column"
            p={3}
            borderRadius={6}
            boxShadow={3}
            width={350}
            key={cardKey}
            onClick={(e) => {
              if ((e.target as HTMLElement).closest(".copy-ra-button-area")) {
                e.preventDefault();
              } else if (assignment.externalUrl) {
                window.open(
                  assignment.externalUrl,
                  "_blank",
                  "noopener,noreferrer"
                );
              }
            }}
            sx={{
              textDecoration: "none",
              color: "inherit",
              cursor: assignment.externalUrl ? "pointer" : "default",
              position: "relative",
            }}
          >
            <Typography variant="h3" align="center" color="primary">
              {assignment.title}
            </Typography>
            {assignment.description && (
              <Typography
                variant="subtitle1"
                align="center"
                color="textSecondary"
                sx={{ mt: 1, mb: 1 }}
              >
                {assignment.description}
              </Typography>
            )}
            {assignment.studentSeducId && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                mt={1}
                className="copy-ra-button-area"
                onClick={(e) => {
                  e.stopPropagation();
                  this.handleCopyRa(raValue, cardKey);
                }}
                sx={{
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: grey[100],
                  },
                }}
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontWeight: "bold", mr: 1 }}
                >
                  RA: {raValue}
                </Typography>
                <Tooltip
                  title={isCopied ? "Copiado!" : "Copiar RA"}
                  placement="top"
                >
                  <IconButton size="small" aria-label="copiar RA">
                    {isCopied ? (
                      <CheckIcon fontSize="small" color="success" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Box height={50} mt={1} />
          </Box>
        );
      } else {
        const isSolved = !!assignment.finishedAt;
        const dueDateInternal = assignment.dueDate
          ? new Date(assignment.dueDate)
          : new Date();
        const isLate = !assignment.finishedAt && dueDateInternal < new Date();

        const redirectLink = isSolved
          ? `/instrument/end`
          : isLate
            ? undefined
            : `/instructions/${assignment.assignmentId!}`;
        let redirectState = null;
        if (isSolved) {
          redirectState = {
            data: {
              assignmentId: assignment.assignmentId,
              instrumentId: assignment.instrumentId,
              instrumentTitle: assignment.title,
              firstTimeFinished: false,
            },
          };
        } else {
          redirectState = {
            data: {
              instrumentId: assignment.instrumentId,
            },
          };
        }
        return (
          <Box
            mt={3}
            border="solid 1px"
            borderColor={grey[300]}
            display="flex"
            flexDirection="column"
            p={3}
            borderRadius={6}
            boxShadow={3}
            width={350}
            key={cardKey}
            component={redirectLink ? Link : "div"}
            {...(redirectLink
              ? {
                to: redirectLink,
                state: redirectState,
                style: { textDecoration: "none" },
              }
              : {})}
            state={redirectState}
            style={{ textDecoration: "none" }}
          >
            <Typography variant="h3" align="center" color="primary">
              {assignment.title}
            </Typography>
            {assignment.description && (
              <Typography
                variant="subtitle1"
                align="center"
                color="textSecondary"
                sx={{ mt: 1, mb: 1 }}
              >
                {assignment.description}
              </Typography>
            )}
            <Typography
              variant="subtitle1"
              align="center"
              color="textSecondary"
              sx={{ mt: 1 }}
            >
              {isSolved
                ? `Respondido em ${new Date(
                  assignment.finishedAt!
                ).toLocaleDateString()}`
                : isLate
                  ? `Expirado em ${dueDateInternal.toLocaleDateString()}`
                  : `Disponível até ${dueDateInternal.toLocaleDateString()}`}
            </Typography>
            <Box alignSelf={"flex-end"} width={50} height={50} mt={1}>
              {isSolved ? (
                <Verified color="primary" sx={{ width: 50, height: 50 }} />
              ) : isLate ? (
                <Report color="error" sx={{ width: 50, height: 50 }} />
              ) : (
                <Schedule
                  sx={{ width: 50, height: 50, color: "text.secondary" }}
                />
              )}
            </Box>
          </Box>
        );
      }
    });

    return cards;
  }

  render() {
    return (
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        alignItems="center"
        justifyContent="center"
      >
        <LoginGuard />
        <Header logoutEnabled={true} />
        {this.state.isLoading && this.renderLoading()}

        {!this.state.isLoading &&
          this.state.error.message &&
          this.renderStateMessage("Desculpe, houve um erro", this.state.error.message!, {
            isError: true,
          })}

        {!this.state.isLoading &&
          !this.state.error.message &&
          this.renderAssignmentsCards()}
      </Box>
    );
  }
}

export default withNavigationHooks(Assignemnt);
